import React, { useState, useEffect } from "react";
import {
  Modal,
  Box,
  Typography,
  Button,
  TextField,
  IconButton,
  Stack,
  CircularProgress,
  Divider,
} from "@mui/material";
import GoogleIcon from "@mui/icons-material/Google";
import CloseIcon from "@mui/icons-material/Close";
import { supabase } from "../../utils/supabaseClient"; // Ensure you have the Supabase client initialized
import { useTheme } from "@mui/material/styles";
import { Checkbox, FormControlLabel } from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom"; // Add useLocation if not there
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined"; // Add this icon
import ArrowRightAltIcon from "@mui/icons-material/ArrowRightAlt";

// --- Define constants for plan IDs ---
const PLAN_MONTHLY = "monthly";
const PLAN_ANNUAL = "annual";

// --- IMPORTANT: Update Prices Here (used in plan boxes, button text & left panel) ---
const PRICE_MONTHLY = "$4.99 USD"; // Replace with your actual monthly price
const PRICE_ANNUAL = "$49.99 USD"; // Replace with your actual annual price
const PRICE_ANNUAL_PERIOD = "/ year";
const PRICE_MONTHLY_PERIOD = "/ month";
const ANNUAL_SAVING_TEXT = "Save 17%"; // Replace with your actual saving text/amount
// ---

// --- SignUpModal Component ---
const SignUpModal = ({ open, onClose, switchToLogin }) => {
  const theme = useTheme();
  const navigate = useNavigate();

  // --- State Variables ---
  const [selectedPlan, setSelectedPlan] = useState(PLAN_MONTHLY);
  const [isLoading, setIsLoading] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [agreeToTerms, setAgreeToTerms] = useState(false);
  const [message, setMessage] = useState("");
  const [emailError, setEmailError] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [termsError, setTermsError] = useState("");
  const [error, setError] = useState("");

  // --- Reset State When Modal Closes ---
  useEffect(() => {
    if (!open) {
      setMessage("");
      setError("");
      setEmail("");
      setPassword(""); // Reset password field too
      setSelectedPlan(PLAN_MONTHLY);
      setIsLoading(false);
    }
  }, [open]);

  // Helper to get country from IP
  const detectUserCountry = async () => {
    try {
      // 1. Get IP
      const ipResponse = await fetch("https://api64.ipify.org?format=json");
      const ipData = await ipResponse.json();

      // 2. Get Country
      const geoResponse = await fetch(
        `http://ip-api.com/json/${ipData.ip}?fields=country`,
      );
      const geoData = await geoResponse.json();

      return geoData.country || "Unknown";
    } catch (error) {
      console.error("Failed to detect country:", error);
      return "Unknown";
    }
  };

  // --- MODIFIED Handler: Validate Email, Call Edge Function, Redirect ---
  // --- Handler for "Sign Up (Inactive) -> Pay -> Activate" model ---
  const handleFinalSubmit = async () => {
    setMessage("");
    let hasError = false;
    setEmailError("");
    setPasswordError("");
    setTermsError("");

    // --- Email validation ---
    if (!email) {
      setEmailError("Email is required.");
      hasError = true;
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      setEmailError("Please enter a valid email.");
      hasError = true;
    }

    // --- Password validation ---
    if (!password) {
      setPasswordError("Password is required.");
      hasError = true;
    } else if (password.length < 6) {
      setPasswordError("Password must be at least 6 characters.");
      hasError = true;
    }

    // --- Terms checkbox validation ---
    if (!agreeToTerms) {
      setTermsError("You must agree before continuing.");
      hasError = true;
    }

    // --- Stop if any error exists ---
    if (hasError) return;

    // --- Proceed with Sign Up and Payment ---
    setIsLoading(true);

    try {
      // 1. *** Sign up the user in Supabase Auth ***
      const detectedCountry = await detectUserCountry();
      const { data: signUpData, error: signUpError } =
        await supabase.auth.signUp({
          email,
          password,
          // 👇 ADD THIS: Send metadata so the trigger can use it
          options: {
            data: {
              country: detectedCountry, // Or capture from input/IP
              // has_paid: false,       // Don't send this here; let the DB default handle it for security
            },
          },
        });

      if (signUpError) {
        // Handle signup errors (e.g., user already exists)
        console.error("[SignUp] Supabase auth signUp failed:", signUpError);
        if (signUpError.message.includes("User already registered")) {
          setError(
            <>
              ❌ An account with this email already exists.{" "}
              <span
                onClick={switchToLogin}
                style={{
                  color: theme.palette.primary.main,
                  fontWeight: "bold",
                  cursor: "pointer",
                  textDecoration: "underline",
                }}
              ></span>
            </>,
          );

          setTimeout(() => {
            const link = document.getElementById("login-link-existing");
            if (link) link.addEventListener("click", switchToLogin);
          }, 100);
        } else {
          setError(`❌ Sign up failed: ${signUpError.message}`);
        }
        setIsLoading(false);
        return;
      }

      // Check if signup was successful and we have user data
      if (!signUpData.user) {
        // Should not happen if signUpError is null, but check just in case
        console.error(
          "[SignUp] Signup seemed successful but no user data returned.",
        );
        setError("❌ An unexpected error occurred during signup.");
        setIsLoading(false);
        return;
      }

      // 2. *** Call Edge Function to get Stripe Checkout URL ***
      const { data: functionData, error: functionError } =
        await supabase.functions.invoke(
          "create-stripe-checkout", // Call the function to create the session
          {
            body: JSON.stringify({ email: email, plan: selectedPlan, theme: localStorage.getItem("themeMode") || "dark" }), // Pass email/plan/theme for metadata
          },
        );

      if (functionError || functionData.error) {
        const errMsg =
          functionError?.message || functionData?.error || "Unknown error";
        console.error(
          "[SignUp] Error invoking/returned from 'create-stripe-checkout' function:",
          errMsg,
        );
        // Provide feedback, maybe suggest contacting support if signup worked but checkout failed
        setError(
          `❌ Account created, but couldn't initiate payment: ${errMsg}. Please try logging in or contact support.`,
        );
        setIsLoading(false);
        return;
      }

      // 3. *** Redirect to Stripe ***
      // 3. *** Redirect to Stripe ***
      if (functionData.checkout_url) {
        window.location.href = functionData.checkout_url;
      } else {
        console.error(
          "[SignUp] Edge Function response missing 'checkout_url'.",
          functionData,
        );
        setError(
          "❌ Account created, but failed to get checkout session. Please contact support.",
        );
        setIsLoading(false);
      }
    } catch (err) {
      // Catch unexpected errors
      console.error("[SignUp] Unexpected error in handleFinalSubmit:", err);
      setError(`❌ An unexpected error occurred: ${err.message}`);
      setIsLoading(false);
    }
  };

  // --- You would put this handleFinalSubmit function into your Signup.js component ---
  // --- replacing the previous one. The rest of the Signup.js code remains the same. ---

  // --- Google Sign-In Handler (Needs Adjustment for New Flow) ---
  // This would also need to change. Instead of signing in directly,
  // it might get the user's email via Google Auth, then call your
  // 'create-stripe-checkout' Edge Function with that email.
  const handleGoogleSignIn = async () => {
    setIsLoading(true);
    try {
      // Persist the selected plan so Layout can redirect to Stripe after OAuth callback
      localStorage.setItem("flybnb_pending_plan", selectedPlan);

      const { error } = await supabase.auth.signInWithOAuth({
        provider: "google",
        options: {
          redirectTo: window.location.origin,
        },
      });

      if (error) throw error;
      // Browser redirects to Google — no code runs after this
    } catch (error) {
      localStorage.removeItem("flybnb_pending_plan");
      console.error("Google Sign-In Error:", error.message);
      setError(`Google Login Failed: ${error.message}`);
      setIsLoading(false);
    }
  };

  const handleComparePlans = () => {
    onClose(); // 1. Close the modal

    // 2. Navigate/Scroll
    // We use a small timeout to let the modal close animation finish
    // so it doesn't conflict with the scroll.
    setTimeout(() => {
      const section = document.getElementById("subscribe-section");
      if (section) {
        section.scrollIntoView({ behavior: "smooth" });
      } else {
        // If not on homepage, go there first
        navigate("/#subscribe-section");
      }
    }, 100);
  };

  // --- Render JSX (Largely the same, password field might be removed later) ---
  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="signup-modal-title"
      aria-describedby="signup-modal-description"
      sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}
    >
      <Box
        sx={{
          width: { xs: "95%", sm: "800px" },
          maxWidth: "800px",
          maxHeight: "90vh",
          overflowY: "auto",
          bgcolor: "background.paper",
          borderRadius: "12px",
          display: "flex",
          flexDirection: "column",
          boxShadow: 6,
          p: { xs: 2, md: 3 },
          position: "relative",
        }}
      >
        {/* --- Close Button --- */}
        <IconButton
          onClick={onClose}
          sx={{
            position: "absolute",
            top: { xs: 8, md: 12 },
            right: { xs: 8, md: 12 },
            color: "text.secondary",
          }}
          aria-label="Close signup modal"
          disabled={isLoading}
        >
          <CloseIcon />
        </IconButton>

        {/* --- Logo --- */}
        <Box sx={{ width: "100%", textAlign: "center", pt: 1, pb: 2 }}>
          <img
            src={
              theme.palette.mode === "dark"
                ? "/logo/dark-main-logo.svg"
                : "/logo/light-main-logo.svg"
            }
            alt="FlyBnB Logo"
            style={{ width: "180px", height: "auto" }}
          />
        </Box>

        {/* --- Main Content Row --- */}
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            width: "100%",
            flexGrow: 1,
          }}
        >
          {/* --- Left Section - Benefits --- */}
          <Box
            sx={{
              width: { xs: "100%", md: "45%" },
              pr: { xs: 0, md: 3 },
              mb: { xs: 3, md: 0 },
              borderRight: {
                xs: "none",
                md: `1px solid ${theme.palette.divider}`,
              },
              display: "flex",
              flexDirection: "column",
              ml: 1,
            }}
          >
            <Typography
              id="signup-modal-title"
              variant="subtitle1"
              component="div"
              fontWeight="bold"
              sx={{
                textAlign: { xs: "center", md: "left" },
                fontSize: "1rem",
                mb: 0.5,
              }}
            >
              Search smarter. Travel better.
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                textAlign: { xs: "center", md: "left" },
                mb: 2,
                fontSize: "0.8rem",
              }}
            >
              Everything you need to plan in one place.
            </Typography>
            <Stack spacing={1.8} sx={{ pl: { xs: 1, md: 0 } }}>
              {[
                { emoji: "🛫🏠", text: "Flights + Airbnb, searched together" },
                {
                  emoji: "🎯",
                  text: "Powerful filters — stops, ratings, amenities",
                },
                { emoji: "🧠", text: "AI reads the reviews. You just pick." },
                { emoji: "🚀", text: "No idea where to go? AI picks for you." },
                { emoji: "🗺️", text: "Browse listings on an interactive map" },
                { emoji: "🌍", text: "Prices always in your local currency" },
              ].map(({ emoji, text }) => (
                <Box
                  key={text}
                  sx={{ display: "flex", alignItems: "flex-start", gap: 1.2 }}
                >
                  <Typography
                    sx={{
                      fontSize: "1rem",
                      lineHeight: 1,
                      mt: "1px",
                      flexShrink: 0,
                    }}
                  >
                    {emoji}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: "0.855rem",
                      lineHeight: 1.45,
                      color: "text.primary",
                    }}
                  >
                    {text}
                  </Typography>
                </Box>
              ))}
            </Stack>
            {/* 👇 Compare Plans Button 👇 */}
            {/* 👇 Upgraded "Compare Plans" Button 👇 */}
            <Button
              onClick={handleComparePlans}
              endIcon={<ArrowRightAltIcon />}
              disableRipple
              sx={{
                mt: 3,
                alignSelf: { xs: "center", md: "flex-start" },
                textTransform: "none",
                fontWeight: 800, // Extra bold for readability
                fontSize: "0.95rem",
                p: 0, // Remove padding so it sits flush like text
                minWidth: "auto",

                // 🎨 Color Logic: Lighter/Brighter in Dark Mode to pop
                color:
                  theme.palette.mode === "dark"
                    ? "#b39ddb"
                    : theme.palette.primary.main,

                background: "transparent",
                borderBottom: "2px solid transparent", // Reserve space for underline effect if needed

                "&:hover": {
                  background: "transparent",
                  textDecoration: "underline",
                  textUnderlineOffset: "4px", // Modern underline gap

                  // ➡️ Arrow Slide Animation
                  "& .MuiButton-endIcon": {
                    transform: "translateX(6px)",
                  },
                },

                // Smooth transition for the arrow movement
                "& .MuiButton-endIcon": {
                  transition: "transform 0.2s ease-in-out",
                  marginLeft: 1,
                },
              }}
            >
              See all features
            </Button>
          </Box>

          {/* --- Right Section - Form --- */}
          <Box
            sx={{
              width: { xs: "100%", md: "55%" },
              pl: { xs: 0, md: 3 },
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <Stack
              spacing={2}
              sx={{
                width: "100%",
                maxWidth: "380px",
                mx: "auto",
                textAlign: "center",
              }}
            >
              {/* Title adjusted for new flow */}
              <Typography variant="h5" component="h2" fontWeight="bold">
                Create your account
              </Typography>
              {/* Google Button (Functionality needs update) */}
              <Button
                variant="outlined"
                fullWidth
                startIcon={<GoogleIcon />}
                onClick={handleGoogleSignIn}
                disabled={isLoading}
                sx={{
                  borderColor: "divider",
                  color: "text.primary",
                  textTransform: "none",
                  "&:hover": { bgcolor: "action.hover" },
                }}
              >
                Continue with Google
              </Button>
              <Divider sx={{ fontSize: "0.9rem", color: "text.secondary" }}>
                OR
              </Divider>
              {/* Email Input */}
              <TextField
                fullWidth
                id="signup-email"
                label="E-mail Address"
                variant="outlined"
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={isLoading}
                aria-required="true"
                error={Boolean(emailError)}
                helperText={emailError}
                inputProps={{ style: { fontSize: "16px" } }}
              />
              {/* Password Input (Kept for now, maybe remove later) */}
              <TextField
                fullWidth
                error={Boolean(passwordError)}
                helperText={passwordError}
                id="signup-password"
                label="Password" // Adjust label maybe
                type="password"
                variant="outlined"
                autoComplete="new-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={isLoading}
                inputProps={{ style: { fontSize: "16px" } }}
                // aria-required="true"
              />
              <Box sx={{ mt: 1, mb: 0 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={agreeToTerms}
                      onChange={(e) => setAgreeToTerms(e.target.checked)}
                      name="agreeToTerms"
                      disabled={isLoading}
                      sx={{ alignSelf: "flex-start" }}
                    />
                  }
                  label={
                    <Typography
                      variant="body2"
                      sx={{
                        color: "text.secondary",
                        textAlign: "left",
                        lineHeight: 1.5,
                        "& a": {
                          color:
                            theme.palette.mode === "dark"
                              ? "#90caf9"
                              : theme.palette.primary.main,
                          textDecoration: "underline",
                          "&:hover": {
                            color:
                              theme.palette.mode === "dark"
                                ? "#bbdefb"
                                : theme.palette.primary.dark,
                          },
                        },
                      }}
                    >
                      I agree to the{" "}
                      <a
                        href="/termsofservice"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Terms of Service
                      </a>{" "}
                      and{" "}
                      <a
                        href="/privacy"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Privacy Policy
                      </a>
                    </Typography>
                  }
                />
              </Box>
              {termsError && (
                <Typography
                  variant="caption"
                  sx={{
                    color: "error.main",
                    mt: "-4px", // pulls it up slightly
                    ml: "36px", // aligns with checkbox label
                    textAlign: "left",
                    fontSize: "0.8125rem", // match MUI TextField helper text
                  }}
                >
                  {termsError}
                </Typography>
              )}

              {/* Plan Selection */}
              <Typography
                variant="subtitle1"
                fontWeight="medium"
                sx={{ pt: 1 }}
              >
                Choose Your Plan:
              </Typography>
              <Stack direction="row" spacing={2} sx={{ width: "100%" }}>
                <Box
                  role="button"
                  aria-pressed={selectedPlan === PLAN_MONTHLY}
                  tabIndex={isLoading ? -1 : 0}
                  onClick={() => !isLoading && setSelectedPlan(PLAN_MONTHLY)}
                  onKeyPress={(e) =>
                    (e.key === "Enter" || e.key === " ") &&
                    !isLoading &&
                    setSelectedPlan(PLAN_MONTHLY)
                  }
                  sx={{
                    flex: 1,
                    p: 1.5,
                    textAlign: "center",
                    borderRadius: 2,
                    border: `2px solid ${selectedPlan === PLAN_MONTHLY ? theme.palette.primary.main : theme.palette.divider}`,
                    bgcolor:
                      selectedPlan === PLAN_MONTHLY
                        ? theme.palette.action.selected
                        : "transparent",
                    boxShadow:
                      selectedPlan === PLAN_MONTHLY ? theme.shadows[2] : "none",
                    cursor: isLoading ? "default" : "pointer",
                    transition:
                      "border-color 0.2s, background-color 0.2s, box-shadow 0.2s",
                    "&:hover": {
                      borderColor: isLoading
                        ? undefined
                        : theme.palette.primary.light,
                      bgcolor: isLoading
                        ? undefined
                        : selectedPlan === PLAN_MONTHLY
                          ? theme.palette.action.selected
                          : theme.palette.action.hover,
                    },
                  }}
                >
                  <Typography
                    variant="h6"
                    component="div"
                    sx={{ fontSize: "1rem" }}
                  >
                    Monthly
                  </Typography>
                  <Typography variant="body2" fontWeight="bold">
                    {PRICE_MONTHLY}
                  </Typography>
                  <Typography
                    variant="caption"
                    display="block"
                    color="text.secondary"
                    sx={{ lineHeight: 1.2 }}
                  >
                    {PRICE_MONTHLY_PERIOD}
                  </Typography>
                </Box>
                <Box
                  role="button"
                  aria-pressed={selectedPlan === PLAN_ANNUAL}
                  tabIndex={isLoading ? -1 : 0}
                  onClick={() => !isLoading && setSelectedPlan(PLAN_ANNUAL)}
                  onKeyPress={(e) =>
                    (e.key === "Enter" || e.key === " ") &&
                    !isLoading &&
                    setSelectedPlan(PLAN_ANNUAL)
                  }
                  sx={{
                    flex: 1,
                    p: 1.5,
                    textAlign: "center",
                    borderRadius: 2,
                    position: "relative",
                    border: `2px solid ${selectedPlan === PLAN_ANNUAL ? theme.palette.primary.main : theme.palette.divider}`,
                    bgcolor:
                      selectedPlan === PLAN_ANNUAL
                        ? theme.palette.action.selected
                        : "transparent",
                    boxShadow:
                      selectedPlan === PLAN_ANNUAL ? theme.shadows[2] : "none",
                    cursor: isLoading ? "default" : "pointer",
                    transition:
                      "border-color 0.2s, background-color 0.2s, box-shadow 0.2s",
                    "&:hover": {
                      borderColor: isLoading
                        ? undefined
                        : theme.palette.primary.light,
                      bgcolor: isLoading
                        ? undefined
                        : selectedPlan === PLAN_ANNUAL
                          ? theme.palette.action.selected
                          : theme.palette.action.hover,
                    },
                  }}
                >
                  <Typography
                    variant="h6"
                    component="div"
                    sx={{ fontSize: "1rem" }}
                  >
                    Yearly
                  </Typography>
                  <Typography variant="body2" fontWeight="bold">
                    {PRICE_ANNUAL}
                  </Typography>
                  <Typography
                    variant="caption"
                    display="block"
                    color="text.secondary"
                    sx={{ lineHeight: 1.2 }}
                  >
                    {PRICE_ANNUAL_PERIOD}
                  </Typography>
                  {ANNUAL_SAVING_TEXT && (
                    <Typography
                      variant="caption"
                      display="block"
                      sx={{
                        color: "success.main",
                        fontWeight: "medium",
                        mt: 0.2,
                        lineHeight: 1.2,
                      }}
                    >
                      {ANNUAL_SAVING_TEXT}
                    </Typography>
                  )}
                </Box>
              </Stack>

              {/* Proceed to Payment Button */}
              <Button
                variant="contained"
                color="primary"
                onClick={handleFinalSubmit}
                disabled={isLoading}
                fullWidth
                size="large"
                sx={{ mt: 2, fontWeight: "bold", textTransform: "none" }}
              >
                {isLoading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  "Proceed to Payment"
                )}
              </Button>

              {/* Error Display */}
              {error && (
                <Typography
                  id="signup-error-text"
                  variant="body2"
                  sx={{ color: "error.main", mt: 1.5, fontWeight: "medium" }}
                >
                  {error}
                </Typography>
              )}
              {/* Message Display (Might not be needed often here) */}
              {message && (
                <Typography
                  variant="body2"
                  sx={{ color: "primary.main", mt: error ? 0.5 : 1.5 }}
                  dangerouslySetInnerHTML={{ __html: message }}
                />
              )}

              {/* Link to Login (Still relevant) */}
              <Typography
                variant="body2"
                sx={{ color: "text.secondary", pt: 1 }}
              >
                Already have an account?{" "}
                <Typography
                  component="span"
                  onClick={!isLoading ? switchToLogin : undefined}
                  sx={{
                    color: "primary.main",
                    fontWeight: "bold",
                    cursor: isLoading ? "default" : "pointer",
                    textDecoration: "underline",
                    "&:hover": { color: "primary.dark" },
                  }}
                >
                  Log in
                </Typography>
              </Typography>
            </Stack>
          </Box>
        </Box>
      </Box>
    </Modal>
  );
};

export default SignUpModal;
